import { Typography } from '@mui/material';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { formatRange } from 'features/call/vrs-call-comments/helpers';
import type { PreviousCall } from 'features/call/vrs-call-comments/interfaces';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';
import { CallHistoryDropdown } from 'features/ui/components';

interface PreviousCallSelectorProps {
  displayCalls: PreviousCall[];
  selectedCallId: number | undefined;
  selectCall: (id: string | number) => void;
  isPreviousCallsEmpty: boolean;
}

const CallSelectorWrapper = styled('div')`
  width: 100%;
  margin-top: ${theme.spacing(4)};
`;

const ItemRow = styled('div')`
  display: grid;
  grid-template-columns: 1fr 16ch;
  align-items: center;
  width: 100%;
  min-width: 0;
  column-gap: 12px;
`;

const ItemCallId = styled('span')`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const ItemTime = styled('span')`
  opacity: 0.9;
  white-space: nowrap;
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
`;

export const PreviousCallSelector = memo(function PreviousCallSelector({
  displayCalls,
  selectedCallId,
  selectCall,
  isPreviousCallsEmpty,
}: PreviousCallSelectorProps) {
  const items = useMemo(
    () =>
      displayCalls.map((call) => ({
        id: call.callId,
        label: (
          <ItemRow>
            <ItemCallId>{call.callId}</ItemCallId>
            <ItemTime>{formatRange(call.startTime, call.endTime)}</ItemTime>
          </ItemRow>
        ),
      })),
    [displayCalls]
  );

  const { t } = useTranslation();

  // Ensure selectedId is never undefined for the dropdown
  const safeSelectedId =
    selectedCallId !== undefined ? selectedCallId : (items[0]?.id ?? '');

  // Wrap selectCall to cast id to number
  const handleSelectCall = (id: string | number) => {
    if (typeof id === 'string') {
      const num = Number(id);
      if (!isNaN(num)) {
        selectCall(num);
      }
    } else {
      selectCall(id);
    }
  };

  return (
    <CallSelectorWrapper>
      <Typography variant='body1' sx={{ color: theme.palette.grey[50], mb: 2 }}>
        {t('callComments.previousCall')}
      </Typography>

      {!isPreviousCallsEmpty ? (
        <CallHistoryDropdown
          items={items}
          selectedId={safeSelectedId}
          onChange={handleSelectCall}
        />
      ) : (
        <Typography
          variant='body1'
          sx={{
            color: theme.palette.grey[300],
            mb: 3,
            fontStyle: 'italic',
            textAlign: 'center',
          }}
        >
          {t('callComments.noPreviousCallsAvailable')}
        </Typography>
      )}
    </CallSelectorWrapper>
  );
});
